import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, UserCog, Building2, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useDropdown } from '@/hooks/useDropdown';

export function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { user, currentTenant, logout, isLoggingOut } = useAuth();

  // Use the dropdown hook for proper event cleanup
  const menuRef = useDropdown({
    isOpen,
    onClose: () => setIsOpen(false),
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Get initials from user name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Check if upgrade is available
  const currentTier = currentTenant?.tier;
  const TIER_ORDER = ['TINKERER', 'HOBBYIST', 'BUSINESS'];
  const currentTierIndex = currentTier ? TIER_ORDER.indexOf(currentTier) : -1;
  const hasUpgradeAvailable = currentTierIndex >= 0 && currentTierIndex < TIER_ORDER.length - 1;

  return (
    <div ref={menuRef} className="relative">
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="User menu"
        className="
          flex items-center gap-2 rounded-md px-2 py-1.5
          text-foreground-muted hover:bg-background-hover hover:text-foreground
          transition-colors
        "
      >
        {/* Avatar */}
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 text-sm font-medium text-primary" aria-hidden="true">
          {user?.name ? getInitials(user.name) : <User className="h-4 w-4" />}
        </div>
        <div className="hidden xl:block text-left max-w-[200px]">
          <p className="text-sm font-medium text-foreground whitespace-nowrap overflow-hidden text-ellipsis">{user?.name || 'User'}</p>
          <p className="text-xs text-foreground-muted whitespace-nowrap overflow-hidden text-ellipsis">{currentTenant?.tenantName || 'No workspace'}</p>
        </div>
        <ChevronDown className={`hidden xl:block h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          role="menu"
          className="
            absolute right-0 top-full mt-1 w-56 rounded-md border border-border
            bg-background-elevated shadow-lg z-50
          "
        >
          {/* User info */}
          <div className="border-b border-border px-4 py-3" role="presentation">
            <p className="text-sm font-medium text-foreground">{user?.name}</p>
            <p className="text-xs text-foreground-muted truncate">{currentTenant?.tenantName || 'No workspace'}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-xs text-foreground-muted">
                {currentTenant?.tier ? `${currentTenant.tier.charAt(0)}${currentTenant.tier.slice(1).toLowerCase()} Plan` : 'No plan'}
              </p>
              {hasUpgradeAvailable && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/settings/subscription');
                  }}
                  className="text-xs text-primary hover:text-primary/80 hover:underline"
                >
                  Upgrade?
                </button>
              )}
            </div>
          </div>

          {/* Menu items */}
          <div className="py-1">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/settings/profile');
              }}
              role="menuitem"
              className="
                flex w-full items-center gap-2 px-4 py-2 text-sm
                text-foreground-muted hover:bg-background-hover hover:text-foreground
              "
            >
              <UserCog className="h-4 w-4" aria-hidden="true" />
              Profile Settings
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/settings/workspace');
              }}
              role="menuitem"
              className="
                flex w-full items-center gap-2 px-4 py-2 text-sm
                text-foreground-muted hover:bg-background-hover hover:text-foreground
              "
            >
              <Building2 className="h-4 w-4" aria-hidden="true" />
              Workspace Settings
            </button>
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              role="menuitem"
              className="
                flex w-full items-center gap-2 px-4 py-2 text-sm
                text-error hover:bg-error/10
                disabled:opacity-50
              "
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              {isLoggingOut ? 'Logging out...' : 'Log out'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
